/**
 * src/ui/main.ts — A CASCA. O único arquivo que sabe DOM, rede e estado juntos.
 *
 * É daqui que o receptor sobe. Este arquivo NÃO tem regra de produto: quem decide
 * o que tocar é `src/player/engine.ts`, quem busca a fila é `src/api/**`. Aqui só
 * existe encadeamento, e por isso é o arquivo mais seguro de ler.
 *
 * O QUE ESTE ARQUIVO FAZ, EM ORDEM:
 *  1. decide o gateway (`createGateway`) — demo ou rede;
 *  2. monta a superfície de mídia e o MOTOR puro;
 *  3. busca a fila (`If-None-Match`), valida a versão, entrega ao motor;
 *  4. assina o realtime (SSE, caminho primário — D2);
 *  5. liga o watchdog de vídeo e o heartbeat ditado pelo servidor;
 *  6. em `?demo=1`, mostra o painel de debug com botões de inspeção.
 *
 * ⚠️ POLLING DE 5 MIN é OBRIGATÓRIO (D2), não opcional: o SSE pode não existir
 * atrás de proxy, e o produto não pode parar por causa disso.
 */
import { createGateway, isDemoRequested, isDemoBuilt, type ReceptorGateway } from '../api';
import { CredentialStore, browserLocalStorage, type ScreenCredentials } from '../../net';
import { MediaCache, indexedDbBackend, memoryBackend } from '../../net/mediaCache';
import { CachedGateway } from '../api/CachedGateway';
import { ActiveQueue, isCacheUsable, type DisplayItem } from '../player/queue';
import type { QueuePayload } from '../api/types';
import { PlayerEngine, type EngineHost } from '../player/engine';
import { PlayerSurface, MediaUnavailableError } from './player';
import { renderPareado, renderSemAnuncios, renderSemConexao, renderFaixa, renderItemIndisponivel, motivoCurto } from './espera';
import { renderVinculoRecusado as renderVinculoRevogado } from './vinculo';


/** D2: rede de segurança de `GET /queue` quando o SSE não existe. */
export const POLLING_FALLBACK_MS = 5 * 60 * 1000;

/** D4: "Programação pode estar desatualizada" depois de 7 dias sem contato. */
export const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

/** Intervalo de heartbeat até o servidor dizer outro (§3.6). */
const DEFAULT_HEARTBEAT_SECONDS = 60;

/** Diagnóstico. Vai para o console; NUNCA para a TV (§5.2). */
type LogFn = (event: string, detail?: Record<string, unknown>) => void;

/**
 * O EXECUTOR: quem busca a fila, assina o realtime e bate o heartbeat.
 *
 * Isolado do `bootPlayer` de propósito — é a peça que dá para exercitar com o
 * `HttpGateway` real sem DOM. Nenhuma regra de produto aqui: só rede, tempo e o
 * repasse para o motor. NASC UMA VEZ por sessão de player e não é parado por
 * `popstate`: voltar/avançar no histórico não pode derrubar uma TV no ar (§10).
 */
export class Executor {
  private unsubscribe: (() => void) | undefined;
  private fetching = false;
  private fetchAgain = false;
  private knownVersion: string | undefined;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatIntervalSeconds = DEFAULT_HEARTBEAT_SECONDS;
  private stopped = false;
  /** Último contato OK com o backend — base do D4. */
  private lastContactAtMs = Date.now();

  constructor(
    private readonly options: {
      gateway: ReceptorGateway;
      engine: PlayerEngine;
      /** Troca a tela de estado (nunca mostra erro técnico). */
      onState: (node: HTMLElement) => void;
      onLog: LogFn;
      /** `true` quando não há credencial: não busca fila, não bate heartbeat. */
      unpaired: boolean;
      screenLabel?: string;
      onRevoked?: () => void;
    },
  ) {}

  async start(): Promise<void> {
    if (this.options.unpaired) {
      // Sem credencial o app NÃO fica aqui: o roteador manda para `/vincular`
      // (HYBRID §3). Este ramo é só a rede de segurança para o boot direto.
      this.options.onState(renderSemConexao({}));
      return;
    }
    this.options.onState(renderPareado(this.options.screenLabel));
    this.subscribeRealtime();
    if (this.options.gateway instanceof CachedGateway) {
      const cached = await this.options.gateway.restore();
      if (cached && !this.stopped) {
        this.lastContactAtMs = this.options.gateway.lastContactAt;
        await this.onQueuePayload(cached, false);
      }
    }
    if (this.stopped) return;
    this.scheduleHeartbeat();
    void this.pollQueue();
    // Rede de segurança (D2): mesmo com SSE vivo, o polling de 5 min roda.
    this.pollingTimer = setInterval(() => {
      void this.pollQueue();
    }, POLLING_FALLBACK_MS);
  }

  /**
   * `GET /screens/:id/queue` com `If-None-Match`. `null` = 304 = sucesso: o loop
   * segue exatamente igual. Um `200` novo NÃO entra na hora — o motor adia até o
   * item atual terminar (§4.3, "sem cortar o anúncio").
   */
  async pollQueue(): Promise<void> {
    if (this.stopped) {
      return;
    }
    if (this.fetching) { this.fetchAgain = true; return; }
    this.fetching = true;
    try {
      const payload = await this.options.gateway.fetchQueue(this.knownVersion);
      if (this.stopped) {
        return;
      }
      if (payload === null) {
        // 304: nada muda, nada pisca. Seguimos batendo no intervalo.
        this.lastContactAtMs = Date.now();
        this.options.onLog('queue 304 — nada mudou');
        // A prior all-media failure must not leave a valid cached queue idle forever.
        if (!this.options.engine.isPlaying && this.options.gateway instanceof CachedGateway) {
          const cached = await this.options.gateway.restore();
          if (cached) await this.onQueuePayload(cached);
        }
        return;
      }
      await this.onQueuePayload(payload);
    } catch (error) {
      if (this.stopped) {
        return;
      }
      const code = (error as { code?: string }).code;
      this.options.onLog('fetchQueue falhou', { code });
      if (code === 'DEVICE_TOKEN_REVOKED' || code === 'DEVICE_TOKEN_INVALID') {
        // Critério 10 da §9: tela excluída no app ⇒ apagar credenciais e voltar ao
        // pareamento SOZINHO.
        this.stop();
        this.options.onRevoked?.();
        return;
      }
      // Rede caiu: o motor já tem a fila em memória e segue rodando (§4.4). Só
      // mostramos algo quando NÃO há item no ar.
      if (!this.options.engine.isPlaying) {
        this.options.onState(renderSemConexao({ screenName: this.options.screenLabel }));
      }
    } finally {
      this.fetching = false;
      if (this.fetchAgain && !this.stopped) { this.fetchAgain = false; void this.pollQueue(); }
    }
  }

  /** Um `200` novo: valida a versão (cache corrompido reprova) e manda ao motor. */
  private async onQueuePayload(payload: QueuePayload, online = true): Promise<void> {
    if (!isCacheUsable(payload)) {
      this.options.onLog('fila reprovada em isQueueVersionValid — descartando', {
        version: payload?.version,
      });
      return;
    }
    if (online && this.options.gateway instanceof CachedGateway) await this.options.gateway.prepare(payload);
    if (this.stopped) return;
    if (online) this.lastContactAtMs = Date.now();
    this.knownVersion = payload.version;
    this.options.onLog('fila recebida', { version: payload.version, items: payload.items.length });
    // O motor decide se entra agora ou depois do item atual (§4.3).
    await this.options.engine.setQueue(new ActiveQueue(payload, this.options.gateway.now()));
    if (this.options.gateway instanceof CachedGateway) await this.options.gateway.applied(this.options.engine.currentVersion);
  }

  /** D2: SSE é o caminho primário; se não vier, polling + heartbeat seguram o produto. */
  private subscribeRealtime(): void {
    try {
      this.unsubscribe = this.options.gateway.subscribeQueueUpdates(version => {
        this.options.onLog('realtime queue.updated', { version });
        void this.pollQueue();
      });
    } catch (error) {
      this.options.onLog('realtime indisponível; seguindo com polling', { error: String(error) });
    }
  }

  private scheduleHeartbeat(): void {
    const beat = async (): Promise<void> => {
      if (this.stopped) return;
      try {
        const item = this.options.engine.currentItem;
        const result = await this.options.gateway.heartbeat({
          ...(this.options.engine.currentVersion ? { queueVersion: this.options.engine.currentVersion } : {}),
          ...(item ? { currentAdId: item.adId } : {}),
        });
        if (this.stopped) return;
        this.lastContactAtMs = Date.now();
        if (Number.isFinite(result.heartbeatIntervalSeconds) && result.heartbeatIntervalSeconds > 0)
          this.heartbeatIntervalSeconds = result.heartbeatIntervalSeconds;
        if (result.queueOutdated) await this.pollQueue();
      } catch (error) {
        const code = (error as { code?: string })?.code;
        if (code === 'DEVICE_TOKEN_REVOKED' || code === 'DEVICE_TOKEN_INVALID') {
          this.stop(); this.options.onRevoked?.(); return;
        }
        this.options.onLog('heartbeat falhou', { code });
      } finally {
        if (!this.stopped) this.heartbeatTimer = setTimeout(() => { void beat(); }, this.heartbeatIntervalSeconds * 1000);
      }
    };
    this.stopHeartbeat();
    void beat();
  }

  /** `true` quando passou D4 (7 dias) sem contato — mostra a faixa discreta. */
  isStale(nowMs: number = Date.now()): boolean {
    return nowMs - this.lastContactAtMs > STALE_AFTER_MS;
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  stop(): void {
    this.stopped = true;
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.stopHeartbeat();
    if (this.pollingTimer !== null) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }
}

/**
 * Sobe o PLAYER com a credencial de tela. É a divisão de `bootReceptor` pedida no
 * HYBRID §9: o pareamento por código saiu daqui (o web não tem mais código de
 * 6 dígitos) e a decisão de modo passou para `ui/app.ts`.
 *
 * A credencial chega por parâmetro: o app já a leu do disco e já decidiu o modo.
 * Quando não vem (chamada direta de teste/`?demo=1`), o próprio player a busca —
 * e se não houver nenhuma, ele mostra "sem conexão" em vez de ficar mudo.
 *
 * ⚠️ O `#overlay` é RECRIADO aqui, mas os elementos de mídia (`#surface-image`/
 * `#surface-video`) NÃO: eles já existem no `index.html` e são a identidade do
 * player. Este arquivo nunca toca em `#app` além de acrescentar o overlay.
 */
export function bootPlayer(doc: Document = document, paired?: ScreenCredentials): void {
  const root = doc.getElementById('app');
  if (!root) {
    return;
  }

  const storage = browserLocalStorage();
  const isDemo = isDemoRequested(globalThis.location?.search ?? '', globalThis.location?.hash ?? '') || isDemoBuilt();
  const identity = paired ?? (storage ? new CredentialStore(storage).load() : null);
  const bundle = createGateway({ demo: isDemo, ...(identity ?? {}) });
  const unpaired = !isDemo && identity === null;
  let revoked = false;
  let executor: Executor;
  const revoke = (): void => {
    if (revoked) return;
    revoked = true; executor.stop(); void engine.stop(); void surface?.clear('new-queue-empty');
    if (storage) {
      new CredentialStore(storage).clear();
    }
    onState(renderVinculoRevogado());
    // A credencial morreu: o fluxo de vínculo recomeça (HYBRID §6). Quem
    // redireciona é o `app.ts`, dono do roteador.
  };
  const cache = identity && !isDemo ? new CachedGateway(bundle.gateway,
    new MediaCache({ backend: indexedDbBackend(`simpleindor-${identity.screenId}`) ?? memoryBackend() }), identity.screenId, revoke) : null;
  const gateway = cache ?? bundle.gateway;

  const overlay = doc.createElement('div');
  overlay.id = 'overlay';
  root.append(overlay);

  const log: LogFn = (event, detail) => {
    console.debug('[receptor]', event, detail ?? '');
  };
  if (bundle.warning) {
    console.warn('[receptor]', bundle.warning);
  }

  let surface: PlayerSurface | null = null;
  const onState = (node: HTMLElement): void => {
    overlay.replaceChildren(node);
  };

  const host: EngineHost = {
    show: async command => {
      if (!surface) {
        throw new Error('player: superfície não montada');
      }
      try {
        overlay.replaceChildren(); // sai da tela de estado, sem desmontar a mídia
        await surface.show(command);
      } catch (error) {
        // Item que não pode ser exibido (PLAYER §6): pula e REPORTA ao lojista.
        // A rejeição do `show` é o que o motor usa para avançar; sem devolver
        // o erro ele marcaria o item como exibido. Mostramos o motivo
        // TRANSLÚCIDO por alguns segundos (caso a fila inteira falhe) ou
        // instantaneamente (caso seja o único item ruim) e em seguida
        // devolvemos o erro pro motor pular. O overlay some na próxima
        // chamada a `show` (substituição por `replaceChildren()`).
        const motivo =
          error instanceof MediaUnavailableError
            ? motivoCurto(error.reason)
            : 'mídia indisponível';
        const adId = command.item.adId;
        log('item pulado (mídia indisponível)', { adId, motivo, error: String(error) });
        onState(renderItemIndisponivel({ motivo, adId }));
        throw error;
      }
    },
    clear: async () => {
      if (surface) {
        await surface.clear('new-queue-empty');
      }
      // Fila vazia ⇒ tela de espera com nome da tela (nunca preta — §6/§4.6).
      onState(renderSemAnuncios({ screenName: bundle.screenLabel }));
    },
    onLog: log,
    onItemChanged: item => {
      log('item', { adId: item?.adId ?? null });
      if (cache) void cache.applied(engine.currentVersion).catch(() => log('cache indisponível'));
    },
  };

  surface = new PlayerSurface({
    root,
    gateway,
    onEnded: reason => {
      void engine.notifyItemEnded(reason);
    },
    onLog: log,
  });

  const engine = new PlayerEngine({ clock: { nowMs: () => gateway.now().getTime() }, host });
  surface.setVideoWatchdog(() => engine.videoWatchdogExpired());

  executor = new Executor({
    gateway,
    engine,
    onState,
    onLog: log,
    unpaired,
    ...(bundle.screenLabel ? { screenLabel: bundle.screenLabel } : {}),
    onRevoked: revoke,
  });

  void executor.start();

  // D4: faixa discreta se ficou 7 dias sem contato. O motor já tem a fila; isto é
  // aviso, não bloqueio.
  const scheduleTimer = setInterval(() => { void engine.revalidateCurrent(); }, 1000);
  const staleTimer = setInterval(() => {
    if (executor.isStale() && engine.isPlaying && !overlay.querySelector('[data-papel="faixa"]')) {
      overlay.append(renderFaixa('Programação pode estar desatualizada', 'neutro'));
    }
  }, 60 * 1000);

  globalThis.addEventListener?.('pagehide', () => { executor.stop(); void engine.stop(); clearInterval(staleTimer); clearInterval(scheduleTimer); void surface?.clear('new-queue-empty'); void surface?.destroy(); }, { once: true });
  if (isDemo) {
    installDemoDebug(doc, root, bundle.gateway, engine, executor, bundle.screenLabel ?? 'TV Demonstração');
  }
}


/**
 * PAINEL DE DEMONSTRAÇÃO (só `?demo=1`): mostra o que está no ar e dá dois botões
 * para inspecionar comportamento sem esperar o gestor:
 *  - "pausar anúncio" → muda a fila; o motor deve aplicar SEM cortar o atual (§4.3);
 *  - "rebuscar fila" → força um `GET /queue` (com `If-None-Match`).
 */
function installDemoDebug(
  doc: Document,
  root: HTMLElement,
  gateway: ReceptorGateway,
  engine: PlayerEngine,
  executor: Executor,
  screenLabel: string,
): void {
  const panel = doc.createElement('pre');
  panel.className = 'demo-debug';
  panel.id = 'demo-debug';
  root.append(panel);

  const stats = (): Record<string, number> | null => {
    const candidate = gateway as unknown as { stats?: () => Record<string, number> };
    return typeof candidate.stats === 'function' ? candidate.stats() : null;
  };

  const render = (): void => {
    const item: DisplayItem | null = engine.currentItem;
    const s = stats();
    const lines = [
      `modo: demonstração    tela: ${screenLabel}`,
      `no ar: ${item ? `${item.adId} (${item.kind})` : '—'}`,
      `fila: ${engine.currentVersion ?? '—'}`,
      `tocando: ${engine.isPlaying ? 'sim' : 'não'}`,
      s ? `GET /queue: ${s['fetchQueue'] ?? 0}   heartbeat: ${s['heartbeat'] ?? 0}` : '',
      'tecla Z esconde este painel',
    ].filter(Boolean);
    panel.textContent = lines.join('\n');
  };
  render();
  const timer = setInterval(render, 500);

  const acoes = doc.createElement('div');
  acoes.className = 'demo-acoes';
  const pauseBtn = doc.createElement('button');
  pauseBtn.textContent = 'pausar 1º anúncio';
  pauseBtn.addEventListener('click', () => {
    const stub = gateway as unknown as { pauseAd?: (id: string) => boolean };
    // Pausa o PRIMEIRO da fila, não o que está no ar: é assim que se prova o §4.3.
    const ok = stub.pauseAd?.('ad_demo_imagem_1') ?? false;
    render();
    if (!ok) {
      panel.textContent += `\n(pausar: ${gateway.constructor.name} sem pauseAd)`;
    }
  });
  const refetchBtn = doc.createElement('button');
  refetchBtn.textContent = 'rebuscar fila';
  refetchBtn.addEventListener('click', () => {
    void executor.pollQueue();
  });
  acoes.append(pauseBtn, refetchBtn);
  root.append(acoes);

  globalThis.addEventListener?.('keydown', event => {
    const key = (event as { key?: string }).key;
    if (key === 'z' || key === 'Z') {
      panel.hidden = !panel.hidden;
      acoes.hidden = panel.hidden;
    }
  });

  // Limpa o timer se a página sair (o `pagehide` roda antes do `unload` em mobile).
  globalThis.addEventListener?.('pagehide', () => clearInterval(timer));
}

